import { getSdInstance } from "../helpers";
import type { AsyncToolHandler } from "../types";
import {
  repairScreenLoadFilterReads,
  type ICompositionRepairNote,
  type ICompositionRepairResult
} from "./workflow-dsl-composition-repair";
import { executeDsl } from "./workflow-dsl-executor";
import { standingDurableLines, supersededConnectionLines, type IStandingSnapshot } from "./workflow-dsl-halt-standing";
import type { DslCommand } from "./workflow-dsl-parser";
import { parseDsl } from "./workflow-dsl-parser";
import { readStandingSnapshot } from "./workflow-dsl-standing-snapshot";
import type { StructuralPartition, ValidationError } from "./workflow-dsl-validator";
import { partitionStructuralErrors, validateDslCommands } from "./workflow-dsl-validator";

/**
 * KOA-18136 §40 — WHAT A HALTED SCRIPT MAY BE FOLLOWED BY.
 *
 * A canvas halt saves nothing after the stop. Live session 889077ca shows
 * what happens when the author then writes a SMALLER script: a 146-line script
 * halted at line 68, the builder re-authored a 68-line one, and the closure step
 * came back with 9 of its 24 parameter mappings — fifteen business fields lost in
 * silence, which is what the user saw as "half the parameters are empty".
 *
 * So the requirement is enforced: while a halt stands, every durable line of the
 * halted script must either be in the next script or already stand on the process.
 * A line missing from both is refused and named. A superset is welcome (that is how
 * corrections arrive), a read-only probe passes without clearing the requirement,
 * and the requirement disappears the moment a script runs without halting.
 *
 * KOA-18593 — "or already stand on the process" is live `202cd605`. The gate used
 * to compare text with text, so after a stop the author did exactly what its
 * teaching says — read the process with LIST, send only what is missing — and five
 * scripts in a row were refused for "dropping" lines whose work stood on the
 * diagram. Whether a line stands is read off the same LIST the author reads
 * (`workflow-dsl-halt-standing.ts`); a line whose kind that cannot read is never
 * forgiven.
 */
interface HaltedScriptRecord {
  /** Durable lines of the script that halted, normalized for comparison. */
  readonly durable: readonly string[];
  readonly line: number;
  /** How many follow-ups have already been refused against this record. */
  refusals: number;
}

let haltedScript: HaltedScriptRecord | null = null;

/**
 * Refusal ceiling. The requirement must never become a permanent lock: if the
 * author cannot or will not resend, three named refusals is where teaching ends
 * and standing in the way begins — the process is then reported unbuilt by the
 * halt gate anyway (`workflow-builder/definition.ts`), which is the honest floor.
 */
const MAX_RESEND_REFUSALS: number = 3;

/** Commands that only LOOK at the graph — a probe made of these changes nothing. */
const READ_ONLY_COMMANDS: ReadonlySet<string> = new Set<string>(["LIST", "LINT", "CAT", "SHOW", "DESCRIBE"]);

/** Comparison form: whitespace collapsed, case kept (ids and labels are case-bearing). */
function normalizeScriptLine(line: string): string {
  return line.trim().replace(/\s+/g, " ");
}

/** The lines of a script that BUILD something — the ones that must not be lost. */
export function durableScriptLines(script: string): string[] {
  const out: string[] = [];
  for (const raw of script.split("\n")) {
    const line: string = normalizeScriptLine(raw);
    if (line.length === 0 || line.startsWith("#") || line.startsWith("//")) continue;
    const verb: string = line.split(/\s+/)[0].toUpperCase();
    if (READ_ONLY_COMMANDS.has(verb)) continue;
    out.push(line);
  }
  return out;
}

/**
 * Durable lines of the halted script that this one no longer has.
 *
 * Multiplicity is not counted: a repeated identical line is the same instruction
 * twice, and demanding the same count would refuse a legitimately de-duplicated
 * resend.
 */
export function droppedDurableLines(halted: readonly string[], next: readonly string[]): string[] {
  const present: Set<string> = new Set<string>(next);
  return halted.filter((line: string): boolean => !present.has(line));
}

/**
 * KOA-18593 — the lines not sent again whose work is also not on the process, and that the next
 * script does not redraw. `halted` is the whole halted script (its creation lines name the aliases
 * the others refer to); `next` is the script being judged — the paths out of an element it draws
 * from are its own to say (`supersededConnectionLines`). With no readable process every line not
 * sent is lost, exactly as before.
 */
export function withoutStandingLines(
  halted: readonly string[],
  notSent: readonly string[],
  snapshot: IStandingSnapshot | undefined,
  next: readonly string[] = []
): string[] {
  if (notSent.length === 0 || snapshot === undefined) return [...notSent];
  const standing: ReadonlySet<string> = standingDurableLines(halted, snapshot);
  const redrawn: ReadonlySet<string> = supersededConnectionLines(halted, next, snapshot);
  return notSent.filter((line: string): boolean => !standing.has(line) && !redrawn.has(line));
}

/**
 * KOA-18593 — ONE PROCESS SCRIPT AT A TIME.
 *
 * The browser starts every tool call the moment it arrives. Live `202cd605`: the transport gave up
 * on a script that was still running, the same script was sent again, and the two ran on the same
 * canvas at once while a LIST read a process that was still being written. Every script now waits
 * for the one before it to finish, whoever sent it — the builder, a deterministic pass, a later turn.
 */
let scriptQueue: Promise<unknown> = Promise.resolve();

/**
 * Async handler for the ui_workflow_dsl tool: queued behind the script before it.
 */
export const handleWorkflowDslCategory: AsyncToolHandler = (input) => {
  const run: Promise<unknown> = scriptQueue.then(
    (): Promise<unknown> => runWorkflowDslScript(input),
    (): Promise<unknown> => runWorkflowDslScript(input)
  );
  scriptQueue = run.catch((): undefined => undefined);
  return run;
};

/**
 * Parses → validates → executes one DSL script.
 */
const runWorkflowDslScript: AsyncToolHandler = async (input) => {
  const { script } = input;

  if (!script || typeof script !== "string") {
    return { error: "Missing required 'script' parameter." };
  }

  const trimmed = script.trim();
  if (trimmed === "") {
    return { error: "Script is empty." };
  }

  // §40 — the halted script's own lines are the contract for the next one.
  if (haltedScript !== null) {
    const durable: string[] = durableScriptLines(trimmed);
    if (durable.length > 0) {
      const notSent: string[] = droppedDurableLines(haltedScript.durable, durable);
      const dropped: string[] = withoutStandingLines(haltedScript.durable, notSent, readStandingSnapshot(), durable);
      if (dropped.length > 0 && haltedScript.refusals < MAX_RESEND_REFUSALS) {
        haltedScript.refusals += 1;
        const digest: string = dropped
          .slice(0, 12)
          .map((line: string): string => `   • ${line}`)
          .join("\n");
        const more: string = dropped.length > 12 ? `\n   • (+${String(dropped.length - 12)} more)` : "";
        return {
          error:
            `Refused: the previous run STOPPED at line ${String(haltedScript.line)} because the process canvas ` +
            `could not be trusted, and nothing after the stop was saved. These ${String(dropped.length)} ` +
            `line(s) of that script are neither in this script nor on the process:\n${digest}${more}\n` +
            `Read the process with LIST and send what it does not hold yet — every line above among it. ` +
            `A line whose work already stands on the process is not sent again, and a line missing from both ` +
            `ships a process without it.`,
          droppedLines: dropped,
          haltedAtLine: haltedScript.line
        };
      }
    }
  }

  // Phase 1: Parse
  const { commands, errors: parseErrors } = parseDsl(trimmed);

  if (parseErrors.length > 0 && commands.length === 0) {
    return {
      error: "Script could not be parsed.",
      parseErrors
    };
  }

  // Phase 1b: deterministic repair, BEFORE validation — KOA-18431.
  //
  // A construct the harness can complete is completed, not refused: the validator's job is what
  // cannot be made to work. `repairScreenLoadFilterReads` moves a read that is fed from a control
  // onto that control's own event, which is the only shape that can carry the value. Running it
  // here is what keeps the rule below quiet on a script it has already fixed.
  const repair: ICompositionRepairResult = repairScreenLoadFilterReads(commands);
  const commandsToRun: DslCommand[] = repair.commands;

  // Phase 2: Static validation.
  //
  // KOA-18136 §33 — a fatal error no longer discards the script. Only the
  // lines that would put a WRONG element on the diagram are dropped; a node
  // whose wiring is merely incomplete is built as written and reported, so the
  // author fixes one line instead of rewriting the whole process. Rejecting
  // everything cost a live run its entire 141-line process (session 947ce0d8).
  const validationErrors: ValidationError[] = validateDslCommands(commandsToRun);
  const validationFatal: ValidationError[] = validationErrors.filter((e: ValidationError) => e.severity === "error");
  const partition: StructuralPartition = partitionStructuralErrors(validationFatal);
  const skipLines: ReadonlySet<number> = new Set<number>(partition.skipLines);
  const executable: DslCommand[] =
    skipLines.size > 0 ? commandsToRun.filter((c: DslCommand): boolean => !skipLines.has(c.line)) : commandsToRun;

  if (executable.length === 0) {
    return {
      error: `Script has ${validationFatal.length} structural error(s) and nothing left to execute.`,
      parseErrors,
      validationErrors,
      hint: STRUCTURAL_HINT
    };
  }

  // Phase 3: Execute (warnings are passed through but don't block execution)
  const sdInstance = getSdInstance();
  const result = await executeDsl(sdInstance, executable, parseErrors);

  // Attach validation warnings to the result
  const validationWarnings = validationErrors.filter((e) => e.severity === "warning");
  // A repair is never silent: the author has to be able to see that the script it sent is not the
  // script that ran, and why — otherwise the next round re-sends the shape that was corrected.
  const repairWarnings: ValidationError[] = repair.notes.map((note: ICompositionRepairNote): ValidationError => ({
    line: note.line,
    rule: "composition-filter-read-retargeted",
    message: note.message,
    severity: "warning"
  }));
  if (validationWarnings.length > 0 || repairWarnings.length > 0) {
    result.validationWarnings = [...validationWarnings, ...repairWarnings];
  }

  // §40 — remember a halt so the next script can be held to it, and forget it the
  // moment a run completes without one.
  const halted: boolean = (result as { canvasHalt?: unknown }).canvasHalt !== undefined;
  if (halted) {
    const haltLine: number = (result as { canvasHalt?: { line?: number } }).canvasHalt?.line ?? 0;
    const durable: string[] = durableScriptLines(trimmed);
    haltedScript = { durable, line: haltLine, refusals: 0 };
  } else if (durableScriptLines(trimmed).length > 0) {
    haltedScript = null;
  }

  if (validationFatal.length > 0) {
    result.structuralIssues = validationFatal;
    result.skippedLines = partition.skipLines;
    // The graph is on the canvas but it is NOT correct yet — the turn must not
    // read as finished while a declared defect stands.
    result.taskComplete = false;
    result.summary = `${result.summary} ${buildStructuralNotice(partition, halted)}`;
  }

  return result;
};

const STRUCTURAL_HINT: string =
  "Common fixes: add XGATEWAY before branching, use PATH only from Gateways (not Tasks), " +
  "ensure each Exclusive Gateway has 1 DEFAULT path and WHEN conditions on non-default paths.";

/**
 * What the author is told after a partial application. Two things matter: the
 * remaining defects are named line by line with their fix, and the author is
 * told NOT to resend the whole script — the sound commands already landed, and
 * a full rewrite is exactly what the all-or-nothing rejection used to force.
 *
 * KOA-18136 §40 — `halted` flips BOTH of those. When the run stopped on a canvas
 * it no longer trusted, nothing after the stop was saved, so "the rest WAS
 * applied / send only the corrections" is false. Live session 889077ca followed
 * this paragraph instead of the halt and lost fifteen parameter mappings, which is
 * the whole reason the flag exists. KOA-18593: what the author sends after a stop is
 * what the process does not hold yet, read with LIST — the same thing the halt and
 * the halt gate ask for.
 */
function buildStructuralNotice(partition: StructuralPartition, halted: boolean = false): string {
  const parts: string[] = [];
  if (partition.reported.length > 0) {
    const digest: string = partition.reported
      .slice(0, 8)
      .map((e: ValidationError): string => `line ${String(e.line)} [${e.rule}] ${e.message}`)
      .join("\n   • ");
    parts.push(
      halted
        ? `STRUCTURAL DEFECTS DECLARED BEFORE EXECUTION (${String(partition.reported.length)}) — this run STOPPED, ` +
            `so nothing about them is settled; correct these lines in what you send next:\n   • ${digest}`
        : `STRUCTURAL DEFECTS REMAIN (${String(partition.reported.length)}) — the rest of the script WAS applied, ` +
            `these elements are on the diagram but wired incorrectly:\n   • ${digest}`
    );
  }
  if (partition.skipped.length > 0) {
    const digest: string = partition.skipped
      .slice(0, 8)
      .map((e: ValidationError): string => `line ${String(e.line)} [${e.rule}] ${e.message}`)
      .join("\n   • ");
    parts.push(
      `SKIPPED (${String(partition.skipped.length)}) — building these would corrupt the diagram, ` +
        `so they were not executed:\n   • ${digest}`
    );
  }
  parts.push(
    halted
      ? "Read the process with LIST, then send every line of this script whose work it does not hold yet, " +
          "with these lines corrected — nothing after the stop was saved. Do NOT drop a line the process does " +
          "not hold, and do NOT send again what already stands."
      : "Send ONLY the corrections in your next script (usually one CONNECT/PATH/DEFAULT line each). " +
          "Do NOT resend the whole script — everything else already landed, and re-creating it will " +
          "collide with what exists."
  );
  return parts.join("\n");
}
